import { db } from '$db/local/db';
import type { OutboxOperation } from '$db/local/db';
import type { UUID } from '$domain/types';
import { getActiveUserId } from '$core/identity/active-user';
import { newId, nowIso, estimatePayloadBytes } from './common';
import { coalesceOutbox } from '../sync/coalesce';

export const OUTBOX_MAX_OPS = 10_000;
export const OUTBOX_MAX_BYTES = 5 * 1024 * 1024;

export interface EnqueueInput {
  entityType: string;
  entityId: string;
  op: 'upsert' | 'delete';
  payload: unknown;
  schemaVersion?: number;
}

export async function enqueueOperation(
  input: EnqueueInput
): Promise<OutboxOperation> {
  const now = nowIso();
  const op: OutboxOperation = {
    id: newId(),
    userId: getActiveUserId(),
    entityType: input.entityType,
    entityId: input.entityId,
    op: input.op,
    payload: input.payload,
    createdAt: now,
    updatedAt: now,
    idempotencyKey: newId(),
    attempts: 0,
    status: 'pending',
    bytes: estimatePayloadBytes(input.payload),
    schemaVersion: input.schemaVersion ?? 1
  };
  await db.outbox.add(op);
  return op;
}

export async function listOutbox(
  userId = getActiveUserId()
): Promise<OutboxOperation[]> {
  const rows = await db.outbox.where('userId').equals(userId).toArray();
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function listOutboxByStatus(
  status: OutboxOperation['status'],
  userId = getActiveUserId()
): Promise<OutboxOperation[]> {
  const rows = await db.outbox.where('userId').equals(userId).toArray();
  return rows
    .filter((op) => op.status === status)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function deleteOutboxOp(id: UUID): Promise<void> {
  await db.outbox.delete(id);
}

export async function deleteOutboxOps(ids: UUID[]): Promise<void> {
  if (ids.length === 0) return;
  await db.outbox.bulkDelete(ids);
}

/**
 * Purga ops cuyo userId no coincide con el activo.
 * Precursor mínimo de M4.4. Las ops guest huérfanas no pueden subir
 * (RLS las rechaza) y bloquean la outbox.
 */
export async function purgeForeignOutbox(
  currentUserId = getActiveUserId()
): Promise<number> {
  const all = await db.outbox.toArray();
  const foreign = all.filter((op) => op.userId !== currentUserId);
  if (foreign.length === 0) return 0;
  await db.outbox.bulkDelete(foreign.map((op) => op.id));
  return foreign.length;
}

export interface OutboxStats {
  count: number;
  bytes: number;
}

export async function getOutboxStats(
  userId = getActiveUserId()
): Promise<OutboxStats> {
  const rows = await listOutbox(userId);
  return {
    count: rows.length,
    bytes: rows.reduce((sum, op) => sum + (op.bytes ?? 0), 0)
  };
}

export interface CoalesceRunResult {
  dropped: number;
  before: number;
  after: number;
}

/**
 * Ejecuta la coalescencia sobre la outbox del usuario activo.
 * Solo toca ops con status 'pending'. Las ops 'syncing', 'failed' y
 * 'quarantined' se preservan intactas.
 */
export async function runCoalescence(): Promise<CoalesceRunResult> {
  const userId = getActiveUserId();
  return db.transaction('rw', db.outbox, async () => {
    const all = await db.outbox.where('userId').equals(userId).toArray();
    const result = coalesceOutbox(all);

    if (result.droppedCount === 0) {
      return {
        dropped: 0,
        before: result.bytesBefore,
        after: result.bytesAfter
      };
    }

    await db.outbox.where('userId').equals(userId).delete();
    if (result.ops.length > 0) {
      await db.outbox.bulkAdd(result.ops);
    }

    return {
      dropped: result.droppedCount,
      before: result.bytesBefore,
      after: result.bytesAfter
    };
  });
}